import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Zap, AlertTriangle, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const L = {
  it: {
    checking: "Verifica del pagamento in corso…",
    success_t: "Abbonamento attivo!",
    success_d: "Grazie, il tuo pagamento è andato a buon fine. Benvenuto in Thoth.",
    pending_t: "Pagamento in elaborazione",
    pending_d: "Il pagamento è ancora in elaborazione. Riceverai conferma a breve.",
    expired_t: "Sessione scaduta",
    expired_d: "La sessione di pagamento è scaduta o è stata annullata. Riprova quando vuoi.",
    goto: "Vai al pannello",
    back: "Torna alla vetrina",
    plan: "Piano",
  },
  en: {
    checking: "Verifying your payment…",
    success_t: "Subscription active!",
    success_d: "Thank you, your payment was successful. Welcome to Thoth.",
    pending_t: "Payment processing",
    pending_d: "Your payment is still processing. You'll get confirmation shortly.",
    expired_t: "Session expired",
    expired_d: "The payment session expired or was cancelled. Try again anytime.",
    goto: "Go to dashboard",
    back: "Back to pricing",
    plan: "Plan",
  },
};

const PLAN_LABEL = {
  thoth_base_monthly: "Base · Mensile", thoth_base_yearly: "Base · Annuale",
  thoth_pro_monthly: "Pro · Mensile", thoth_pro_yearly: "Pro · Annuale",
};

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState(null);
  const pollRef = useRef(0);
  const lang = localStorage.getItem("thoth_lang") || "it";
  const t = L[lang];

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { try { await refresh(); } catch {} setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) { /* keep polling */ }
      pollRef.current += 1;
      if (pollRef.current >= MAX_POLLS) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const paid = state === "paid";
  const checking = state === "checking";

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center fade-up bg-[#141B27] border border-white/8 rounded-3xl p-8 shadow-2xl" data-testid="payment-success-page">
        {checking ? (
          <>
            <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto" />
            <p className="mt-5 text-slate-300" data-testid="payment-checking">{t.checking}</p>
          </>
        ) : paid ? (
          <>
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 grid place-items-center mx-auto">
              <CheckCircle2 className="w-9 h-9 text-emerald-400" />
            </div>
            <h1 className="font-head font-extrabold text-2xl mt-5" data-testid="payment-success-title">{t.success_t}</h1>
            <p className="text-slate-400 mt-2">{t.success_d}</p>
            {info?.lookup_key && (
              <div className="mt-5 inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 text-blue-300 rounded-full px-4 py-1.5 text-sm font-semibold">
                <Zap className="w-4 h-4" fill="currentColor" /> {t.plan}: {PLAN_LABEL[info.lookup_key] || info.lookup_key}
              </div>
            )}
          </>
        ) : state === "pending" ? (
          <>
            <Loader2 className="w-14 h-14 text-amber-400 animate-spin mx-auto" />
            <h1 className="font-head font-extrabold text-2xl mt-5">{t.pending_t}</h1>
            <p className="text-slate-400 mt-2">{t.pending_d}</p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 rounded-2xl bg-amber-500/15 border border-amber-500/30 grid place-items-center mx-auto">
              <AlertTriangle className="w-9 h-9 text-amber-400" />
            </div>
            <h1 className="font-head font-extrabold text-2xl mt-5">{t.expired_t}</h1>
            <p className="text-slate-400 mt-2">{t.expired_d}</p>
          </>
        )}

        {!checking && (
          <div className="mt-7 flex flex-col gap-2">
            {paid ? (
              <button onClick={() => navigate("/admin")} data-testid="payment-goto-dashboard" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 py-3 rounded-xl font-semibold transition-colors">
                {t.goto} <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={() => navigate("/")} data-testid="payment-back-vetrina" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 py-3 rounded-xl font-semibold transition-colors">
                {t.back}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
